import React from "react";

const LAST_UPDATED = "March 2026";

const Section = ({ title, children }) => (
  <div style={{ marginBottom: 40 }}>
    <h2 style={{ fontFamily: "'Nunito', sans-serif", fontWeight: 800, fontSize: 20, color: "#4A2040", marginBottom: 16, paddingBottom: 10, borderBottom: "1px solid #F0DDE8" }}>{title}</h2>
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>{children}</div>
  </div>
);

const Subsection = ({ title, children }) => (
  <div style={{ marginTop: 8 }}>
    <h3 style={{ fontFamily: "'Nunito', sans-serif", fontWeight: 700, fontSize: 16, color: "#4A2040", marginBottom: 8 }}>{title}</h3>
    {children}
  </div>
);

const PrivacyPolicy = ({ onBack }) => {
  return (
    <div style={{ fontFamily: "'DM Sans', -apple-system, sans-serif", background: "#FDF6F9", minHeight: "100vh", color: "#2D1B28" }}>
      <link href="https://fonts.googleapis.com/css2?family=Nunito:wght@700;800;900&family=DM+Sans:wght@400;500;600&display=swap" rel="stylesheet" />

      {/* Nav */}
      <nav style={{ position: "sticky", top: 0, background: "rgba(253,246,249,0.95)", backdropFilter: "blur(12px)", borderBottom: "1px solid #F0DDE8", padding: "0 5%", height: 64, display: "flex", alignItems: "center", justifyContent: "space-between", zIndex: 10 }}>
        <div style={{ fontFamily: "'Nunito', sans-serif", fontWeight: 900, fontSize: 18, color: "#4A2040", display: "flex", alignItems: "center", gap: 8 }}>
          <div style={{ width: 30, height: 30, borderRadius: 8, background: "linear-gradient(135deg, #E8679A, #F48FB1)", display: "flex", alignItems: "center", justifyContent: "center", color: "white", fontWeight: 900, fontSize: 14 }}>S</div>
          ShrewCare
        </div>
        <button onClick={onBack} style={{ background: "none", border: "1px solid #F0DDE8", borderRadius: 8, padding: "8px 16px", fontSize: 13, fontWeight: 600, color: "#8E6C88", cursor: "pointer", fontFamily: "inherit" }}>← Back</button>
      </nav>

      {/* Content */}
      <div style={{ maxWidth: 760, margin: "0 auto", padding: "56px 5% 100px" }}>
        <div style={{ marginBottom: 48 }}>
          <div style={{ display: "inline-block", fontSize: 12, fontWeight: 800, letterSpacing: "1.5px", textTransform: "uppercase", color: "#E8679A", marginBottom: 12 }}>Legal</div>
          <h1 style={{ fontFamily: "'Nunito', sans-serif", fontWeight: 900, fontSize: "clamp(32px, 5vw, 48px)", color: "#4A2040", marginBottom: 12, lineHeight: 1.1 }}>Privacy Policy</h1>
          <p style={{ fontSize: 15, color: "#9E7A90" }}>Last updated: {LAST_UPDATED}</p>
        </div>

        <div style={{ fontSize: 16, lineHeight: 1.8, color: "#3D2A34" }}>
          <Section title="1. Introduction">
            <p>ShrewCare ("we," "us," or "our") operates a school management platform accessible at shrewcare.com (the "Service"). This Privacy Policy explains how we collect, use, disclose, and protect information about you when you use our Service.</p>
            <p>By using ShrewCare, you agree to the collection and use of information in accordance with this policy. If you do not agree, please discontinue use of the Service.</p>
          </Section>

          <Section title="2. Who We Serve">
            <p>ShrewCare is designed for early education and childcare organizations. Our direct users are school administrators, teachers, and parents or guardians. You must be at least 18 years old to create an account or use the Service. The Service is not directed at children, and we do not knowingly collect personal information directly from individuals under 18. Student data entered into the platform is submitted by school administrators and teachers on behalf of enrolled students.</p>
          </Section>

          <Section title="3. Information We Collect">
            <Subsection title="3.1 Account Information">
              <p>When you create an account, we collect your name, email address, and password. School administrators also provide school name, address, phone number, and billing information.</p>
            </Subsection>
            <Subsection title="3.2 School and Student Data">
              <p>School administrators and teachers may enter data about students including names, dates of birth, classroom assignments, attendance records, daily activity logs, and guardian contact information. This data is entered by school staff and belongs to the school.</p>
            </Subsection>
            <Subsection title="3.3 Usage Data">
              <p>We automatically collect information about how you interact with the Service, including pages visited, features used, and timestamps. This helps us improve the platform.</p>
            </Subsection>
            <Subsection title="3.4 Payment Information">
              <p>Billing and payment information is processed by Stripe, our third-party payment processor. We do not store your full credit card number. Please review Stripe's privacy policy at stripe.com/privacy.</p>
            </Subsection>
          </Section>

          <Section title="4. How We Use Your Information">
            <p>We use the information we collect to:</p>
            <ul style={{ paddingLeft: 24, margin: "12px 0" }}>
              {[
                "Provide, operate, and maintain the Service",
                "Process payments and manage subscriptions",
                "Send transactional emails such as account confirmations, password resets, and invitations",
                "Respond to support requests and communications",
                "Improve and develop new features",
                "Comply with legal obligations",
              ].map(item => <li key={item} style={{ marginBottom: 8 }}>{item}</li>)}
            </ul>
            <p>We do not sell your personal information or student data to third parties. We do not use student data for advertising purposes.</p>
          </Section>

          <Section title="5. Data Sharing and Disclosure">
            <p>We share information only in the following limited circumstances:</p>
            <Subsection title="5.1 Service Providers">
              <p>We work with the following trusted third-party vendors to operate our Service. Each is contractually obligated to protect your data and may only use it to provide services to us:</p>
              <ul style={{ paddingLeft: 24, margin: "12px 0" }}>
                <li style={{ marginBottom: 10 }}><strong>Supabase</strong> — database, authentication, and backend infrastructure. <a href="https://supabase.com/privacy" target="_blank" rel="noreferrer" style={{ color: "#E8679A" }}>Privacy Policy</a></li>
                <li style={{ marginBottom: 10 }}><strong>Stripe</strong> — payment processing and subscription management. We do not store your full credit card number. <a href="https://stripe.com/privacy" target="_blank" rel="noreferrer" style={{ color: "#E8679A" }}>Privacy Policy</a></li>
                <li style={{ marginBottom: 10 }}><strong>Resend</strong> — transactional email delivery (account confirmations, password resets, invitations). <a href="https://resend.com/legal/privacy-policy" target="_blank" rel="noreferrer" style={{ color: "#E8679A" }}>Privacy Policy</a></li>
                <li style={{ marginBottom: 10 }}><strong>Vercel</strong> — web hosting and content delivery. <a href="https://vercel.com/legal/privacy-policy" target="_blank" rel="noreferrer" style={{ color: "#E8679A" }}>Privacy Policy</a></li>
              </ul>
            </Subsection>
            <Subsection title="5.2 Legal Requirements">
              <p>We may disclose information if required by law, court order, or governmental authority, or when we believe disclosure is necessary to protect our rights or the safety of users.</p>
            </Subsection>
            <Subsection title="5.3 Business Transfers">
              <p>If ShrewCare is involved in a merger, acquisition, or sale of assets, your information may be transferred as part of that transaction. We will notify you before your data is transferred and becomes subject to a different privacy policy.</p>
            </Subsection>
          </Section>

          <Section title="6. Data Security">
            <p>We implement industry-standard security measures to protect your information, including:</p>
            <ul style={{ paddingLeft: 24, margin: "12px 0" }}>
              {[
                "Encryption of data in transit using TLS/HTTPS",
                "Row-level security ensuring each school's data is fully isolated",
                "Role-based access controls so users only access data they are authorized to view",
                "Secure authentication managed by Supabase",
              ].map(item => <li key={item} style={{ marginBottom: 8 }}>{item}</li>)}
            </ul>
            <p>No method of transmission over the internet is 100% secure. While we strive to protect your data, we cannot guarantee absolute security.</p>
          </Section>

          <Section title="7. Data Retention">
            <p>We retain your account and school data for as long as your account is active or as needed to provide the Service. If you cancel your subscription, your data will be retained for 30 days to allow for reactivation, after which it may be permanently deleted. You may request deletion of your data at any time by contacting us.</p>
          </Section>

          <Section title="8. Your Rights">
            <p>Depending on your location, you may have the following rights regarding your personal information:</p>
            <ul style={{ paddingLeft: 24, margin: "12px 0" }}>
              {[
                "Access — request a copy of the data we hold about you",
                "Correction — request that we correct inaccurate data",
                "Deletion — request that we delete your data",
                "Portability — request your data in a portable format",
                "Objection — object to certain types of processing",
              ].map(item => <li key={item} style={{ marginBottom: 8 }}>{item}</li>)}
            </ul>
            <p>To exercise these rights, contact us at <a href="mailto:support@shrewcare.com" style={{ color: "#E8679A" }}>support@shrewcare.com</a>.</p>
          </Section>

          <Section title="9. Children's Privacy">
            <p>ShrewCare is a business-to-business platform used by schools to manage their operations. All account holders must be at least 18 years old. While we do not collect information directly from children, our platform does store student data — including names, dates of birth, attendance records, and daily activity logs — that is entered by school administrators and teachers on behalf of enrolled students.</p>
            <p>Schools using ShrewCare act as the data controller for student information and are responsible for ensuring they have appropriate authority and parental consent to enter and manage that data in compliance with applicable laws, including COPPA (Children's Online Privacy Protection Act) and any other applicable state or federal regulations. ShrewCare acts as a data processor on behalf of the school and handles student data only as directed by the school.</p>
          </Section>

          <Section title="10. Cookies">
            <p>We use essential cookies and local storage to maintain your session and remember your preferences. We do not use tracking cookies or third-party advertising cookies.</p>
          </Section>

          <Section title="11. Changes to This Policy">
            <p>We may update this Privacy Policy from time to time. We will notify you of significant changes by email or by posting a notice in the Service. Your continued use of the Service after changes take effect constitutes your acceptance of the revised policy.</p>
          </Section>

          <Section title="12. Contact Us">
            <p>If you have questions about this Privacy Policy or our data practices, please contact us at:</p>
            <div style={{ marginTop: 16, padding: "20px 24px", background: "white", borderRadius: 12, border: "1px solid #F0DDE8" }}>
              <p style={{ margin: 0, fontWeight: 600 }}>ShrewCare</p>
              <p style={{ margin: "4px 0 0" }}>Email: <a href="mailto:support@shrewcare.com" style={{ color: "#E8679A" }}>support@shrewcare.com</a></p>
              <p style={{ margin: "4px 0 0" }}>Website: <a href="https://shrewcare.com" style={{ color: "#E8679A" }}>shrewcare.com</a></p>
            </div>
          </Section>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
